import { useState, useEffect, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  RefreshCw, Download, Wallet, Receipt, PiggyBank, Percent,
  ArrowUpRight, ArrowDownRight, ChevronDown, Layers, CalendarPlus,
  Users2, Boxes, HandCoins, Building2, AlertTriangle, ClipboardList, Filter, Tag, LayoutDashboard, MapPin,
} from "lucide-react";
import TermReport from "@/components/TermReport";
import YearSelector from "@/components/YearSelector";
import AgeBreakdown from "@/components/AgeBreakdown";
import CashFlow from "@/components/CashFlow";
import { TERMS, TermFilter } from "@/components/TermFilter";
import { currentTerm } from "@/components/RosterPlanner";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, BarChart, LineChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { fmtCurrency, fmtPercent, fmtNum, FINANCE_PROGRAMMES, PROGRAMME_COLORS, termChipClass, OVERHEAD_CATS, fileBase, fmtAxisMoney } from "@/lib/format";
import { useAuth } from "@/auth/AuthContext";
import { PageHeader, SubTabs, asTabs } from "@/components/PageChrome";

import { nzNow } from "@/lib/dates";
const KPI = ({ label, value, icon: Icon, tone, testId, delay }) => {
  const toneMap = {
    income: "text-[var(--hp-accent)] bg-[var(--hp-accent)]/12",
    expense: "text-[var(--hp-gold)] bg-[var(--hp-gold)]/12",
    profit: "text-[var(--hp-profit)] bg-[var(--hp-primary)]/12",
    margin: "text-[var(--hp-profit)] bg-[var(--hp-primary)]/12",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      data-testid={testId}
      className="relative overflow-hidden rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 hover:border-[var(--hp-border-hi)] transition-colors duration-300"
    >
      <div className="flex items-start justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)]">{label}</p>
        <div className={`grid h-9 w-9 place-items-center rounded-xl ${toneMap[tone]}`}>
          <Icon className="h-[18px] w-[18px]" />
        </div>
      </div>
      <p className="font-mono-num text-base sm:text-lg 2xl:text-base font-bold leading-tight text-[var(--hp-heading)] mt-4 tracking-tight [overflow-wrap:anywhere]">{value}</p>
    </motion.div>
  );
};

function MiniYearSelect({ value, years, onChange, testId, color }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button data-testid={testId} onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-lg border border-[var(--hp-border-hi)] bg-[var(--hp-bg)] px-3 py-1.5 text-sm font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-card-hover)] transition-colors duration-200">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
        {value}
        <ChevronDown className={`h-3.5 w-3.5 text-[var(--hp-muted)] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute z-40 mt-2 w-32 rounded-xl border border-[var(--hp-border-hi)] bg-[var(--hp-sidebar)] p-1.5 shadow-2xl">
            {years.map((y) => (
              <button key={y} data-testid={`${testId}-option-${y}`}
                onClick={() => { onChange(y); setOpen(false); }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                  y === value ? "bg-[var(--hp-primary)] text-[#04231a]" : "text-[var(--hp-text)] hover:bg-[var(--hp-tint-5)]"}`}>
                {y}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function YearComparison({ api, years, currentYear }) {
  const sorted = [...years].sort((a, b) => a - b);
  const [yearA, setYearA] = useState(currentYear);
  const [yearB, setYearB] = useState(sorted.find((y) => y !== currentYear) ?? currentYear);
  const [metric, setMetric] = useState("Profit"); // Profit | Income
  const [data, setData] = useState([]);

  useEffect(() => {
    const other = sorted.find((y) => y !== currentYear);
    if (other !== undefined) setYearB(other);
    setYearA(currentYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [years, currentYear]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`${api}/months`, { params: { year: yearA } }),
          axios.get(`${api}/months`, { params: { year: yearB } }),
        ]);
        if (cancelled) return;
        const key = metric === "Income" ? "income" : "profit";
        const merged = a.data.map((m, i) => ({
          month: m.month,
          [yearA]: m[key],
          [yearB]: b.data[i]?.[key] ?? 0,
        }));
        setData(merged);
      } catch (e) { console.error(e); }
    };
    load();
    return () => { cancelled = true; };
  }, [api, yearA, yearB, metric]);

  const sumA = data.reduce((s, d) => s + (d[yearA] || 0), 0);
  const sumB = data.reduce((s, d) => s + (d[yearB] || 0), 0);
  const delta = sumA - sumB;
  const growth = sumB !== 0 ? (delta / Math.abs(sumB)) * 100 : 0;

  return (
    <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 sm:p-6" data-testid="year-comparison-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
        <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)]">Year-over-Year Comparison</h3>
        {sorted.length >= 2 && (
          <div className="flex flex-wrap items-center gap-2.5">
            <MiniYearSelect value={yearA} years={sorted} onChange={setYearA} testId="compare-year-a" color="#34D399" />
            <span className="text-xs text-[var(--hp-muted)] font-semibold">vs</span>
            <MiniYearSelect value={yearB} years={sorted} onChange={setYearB} testId="compare-year-b" color="#3B82F6" />
            <div className="flex rounded-lg border border-[var(--hp-border-hi)] overflow-hidden">
              {["Profit", "Income"].map((mt) => (
                <button key={mt} data-testid={`compare-metric-${mt.toLowerCase()}`} onClick={() => setMetric(mt)}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                    metric === mt ? "bg-[var(--hp-primary)] text-[#04231a]" : "bg-[var(--hp-bg)] text-[var(--hp-muted)] hover:text-[var(--hp-heading)]"}`}>
                  {mt}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {sorted.length < 2 ? (
        <div className="grid place-items-center py-14 text-center" data-testid="comparison-empty-state">
          <CalendarPlus className="h-9 w-9 text-[var(--hp-muted)] mb-3" />
          <p className="text-sm text-[var(--hp-muted)] max-w-sm">
            Add another year using the year selector above to compare performance side by side and see year-over-year growth.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2 text-sm" data-testid="compare-delta">
            <span className="text-[var(--hp-muted)]">{yearA} total {metric.toLowerCase()}:</span>
            <span className="font-mono-num font-semibold text-[var(--hp-profit)]">{fmtCurrency(sumA)}</span>
            <span className="text-[var(--hp-muted)]">·</span>
            <span className="text-[var(--hp-muted)]">{yearB}:</span>
            <span className="font-mono-num font-semibold text-[var(--hp-accent)]">{fmtCurrency(sumB)}</span>
            <span className={`ml-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold font-mono-num ${
              delta >= 0 ? "bg-[var(--hp-primary)]/12 text-[var(--hp-profit)]" : "bg-[var(--hp-loss)]/12 text-[var(--hp-loss)]"}`}>
              {delta >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
              {delta >= 0 ? "+" : ""}{fmtCurrency(delta)} ({growth >= 0 ? "+" : ""}{growth.toFixed(1)}%)
            </span>
          </div>

          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 360, height: 240 }}>
              <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                <XAxis dataKey="month" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false}
                  tickFormatter={(v) => fmtAxisMoney(v)} />
                <Tooltip contentStyle={{ background: "#0B1120", border: "1px solid #334155", borderRadius: 12, fontSize: 13 }}
                  labelStyle={{ color: "#E2E8F0", fontWeight: 700 }} formatter={(val, name) => [fmtCurrency(val), name]} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Line type="monotone" dataKey={String(yearA)} stroke="#34D399" strokeWidth={2.5} dot={{ r: 3, fill: "#34D399" }} />
                <Line type="monotone" dataKey={String(yearB)} stroke="#3B82F6" strokeWidth={2.5} strokeDasharray="5 4" dot={{ r: 3, fill: "#3B82F6" }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

const tooltipStyleBase = { background: "#0B1120", border: "1px solid #334155", borderRadius: 12, fontSize: 13 };

function TrendLine({ title, data, progs, testId, fmt }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)] mb-3">{title}</p>
      <div className="h-[220px] w-full" data-testid={testId}>
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 360, height: 240 }}>
          <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
            <XAxis dataKey="month" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
            <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={tooltipStyleBase} labelStyle={{ color: "#E2E8F0", fontWeight: 700 }} formatter={(val, name) => [fmt(val), name]} />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            {FINANCE_PROGRAMMES.filter((p) => progs.includes(p)).map((p) => (
              <Line key={p} type="monotone" dataKey={p} stroke={PROGRAMME_COLORS[p]} strokeWidth={2.5} dot={{ r: 3, fill: PROGRAMME_COLORS[p] }} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const marginTone = (m) => (m == null ? "text-[var(--hp-muted)]" : m >= 0.4 ? "text-[var(--hp-profit)] bg-[var(--hp-primary)]/10" : m >= 0.2 ? "text-[var(--hp-c-lime)] bg-[#A3E635]/10" : m >= 0 ? "text-[var(--hp-gold)] bg-[var(--hp-gold)]/10" : "text-[var(--hp-loss)] bg-[var(--hp-loss)]/10");

function MarginHeatmap({ monthlyMargin, progs }) {
  const months = monthlyMargin.map((r) => r.month);
  return (
    <div className="mt-6" data-testid="margin-heatmap">
      <p className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)] mb-3">Margin by Programme & Month</p>
      <div className="overflow-x-auto rounded-xl border border-[var(--hp-border)]">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-[var(--hp-muted)] border-b border-[var(--hp-border)] bg-[var(--hp-bg)]/40">
              <th className="px-3 py-2 text-left font-semibold">Programme</th>
              {months.map((m) => <th key={m} className="px-2 py-2 text-center font-semibold">{m}</th>)}
            </tr>
          </thead>
          <tbody>
            {FINANCE_PROGRAMMES.filter((p) => progs.includes(p)).map((p) => (
              <tr key={p} className="border-b border-[var(--hp-border)]/40" data-testid={`margin-row-${p.replace(/\s+/g, "-").toLowerCase()}`}>
                <td className="px-3 py-2 text-[var(--hp-heading)] whitespace-nowrap"><span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: PROGRAMME_COLORS[p] }} />{p}</span></td>
                {monthlyMargin.map((r) => (
                  <td key={r.month} className="px-1 py-1 text-center">
                    <span className={`inline-block rounded-md px-1.5 py-1 font-mono-num min-w-[46px] ${marginTone(r[p])}`}>{r[p] == null ? "—" : fmtPercent(r[p])}</span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const shareDelta = (cur, prev) => {
  if (prev == null) return <span className="text-[var(--hp-muted)]">—</span>;
  const d = cur - prev;
  const cls = d > 0.005 ? "text-[var(--hp-profit)]" : d < -0.005 ? "text-[var(--hp-loss)]" : "text-[var(--hp-muted)]";
  return <span className={`font-mono-num ${cls}`}>{fmtPercent(prev)} <span className="text-xs">({d >= 0 ? "+" : ""}{(d * 100).toFixed(1)})</span></span>;
};

function ProgrammeBookingCards({ programmes, prefix }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {programmes.map((p) => (
        <div key={p.programme} className="rounded-xl border border-[var(--hp-border)] px-4 py-2.5" data-testid={`${prefix}-${p.programme.replace(/\s+/g, "-").toLowerCase()}`}>
          <div className="flex items-center gap-2 text-sm mb-1">
            <span className="h-2 w-2 rounded-full shrink-0" style={{ background: PROGRAMME_COLORS[p.programme] || "#94A3B8" }} />
            <span className="font-semibold text-[var(--hp-heading)]">{p.programme}</span>
            <span className="ml-auto font-mono-num font-semibold text-[var(--hp-heading)]" data-testid={`${prefix}-${p.programme.replace(/\s+/g, "-").toLowerCase()}-total`}>{p.total}</span>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[var(--hp-muted)]">
            {p.sessions.map((s) => (
              <span key={s.sessionTypeId}>{s.name} <span className="font-mono-num font-semibold text-[var(--hp-heading)]">{s.count}</span></span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function DailyProgrammeBookings({ api, onOpenRoll }) {
  const [date, setDate] = useState(nzNow().toLocaleDateString("en-CA"));
  const [data, setData] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const load = () => axios.get(`${api}/bookings/today-summary`, { params: { date } }).then((r) => { setData(r.data); setFetchedAt(new Date()); }).catch(() => {});
  useEffect(() => { load(); }, [api, date]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return null;
  const dayLabel = new Date(data.date + "T12:00:00").toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long" });
  const isToday = data.date === nzNow().toLocaleDateString("en-CA");
  // Sites is absent on old cached responses (e.g. mid-deploy) -- fall back to the flat view rather
  // than crash. A single site has nothing to break down by, so it renders exactly as before.
  const sites = data.sites || [];
  const multiSite = sites.length > 1;
  return (
    <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 space-y-3" data-testid="daily-programme-bookings">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)]">{isToday ? "Today's bookings" : "Bookings"} · {dayLabel}</h3>
        <input data-testid="daily-bookings-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="rounded-full border border-[var(--hp-border-hi)] bg-[var(--hp-bg)] px-3 py-1.5 text-xs font-semibold text-[var(--hp-heading)] outline-none" />
        {fetchedAt && <span className="text-xs text-[var(--hp-muted)]" data-testid="daily-bookings-asof" title="This doesn't auto-refresh — other screens (kiosk, rolls) may have moved on since">as of {fetchedAt.toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })}</span>}
        <button data-testid="daily-bookings-refresh" onClick={load} title="Refresh" className="grid h-6 w-6 place-items-center rounded-full text-[var(--hp-muted)] hover:text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]"><RefreshCw className="h-3.5 w-3.5" /></button>
        <span className="ml-auto text-xs text-[var(--hp-muted)]"><span className="font-mono-num font-semibold text-[var(--hp-heading)]" data-testid="daily-bookings-total">{data.total}</span> children booked</span>
        {onOpenRoll && <button data-testid="daily-bookings-open-roll" onClick={onOpenRoll} className="rounded-full border border-[var(--hp-border-hi)] px-3 py-1 text-xs font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]">Open the roll →</button>}
      </div>
      {data.total === 0 ? (
        <p className="text-sm text-[var(--hp-muted)]" data-testid="daily-bookings-empty">No confirmed bookings on this day.</p>
      ) : multiSite ? (
        <div className="space-y-4">
          {sites.map((site) => (
            <div key={site.siteId || "unassigned"} data-testid={`daily-bookings-site-${(site.siteId || "unassigned")}`}>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="h-3.5 w-3.5 text-[var(--hp-profit)]" />
                <span className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-heading)]">{site.siteName}</span>
                <span className="font-mono-num text-xs font-semibold text-[var(--hp-muted)]">{site.total} booked</span>
              </div>
              <ProgrammeBookingCards programmes={site.programmes} prefix={`daily-bookings-${site.siteId || "unassigned"}`} />
            </div>
          ))}
        </div>
      ) : (
        <ProgrammeBookingCards programmes={data.programmes} prefix="daily-bookings" />
      )}
    </div>
  );
}

function OperationalMetrics({ api, year }) {
  const [ops, setOps] = useState(null);
  const [inv, setInv] = useState(null);
  useEffect(() => { axios.get(`${api}/reports/operational-summary`, { params: { year } }).then((r) => setOps(r.data)).catch(() => setOps(null)); }, [api, year]);
  useEffect(() => { axios.get(`${api}/invoices/annual`, { params: { year } }).then((r) => setInv(r.data)).catch(() => setInv(null)); }, [api, year]);
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="operational-metrics">
      <KPI testId="op-kpi-enrolled" label="Children Enrolled" tone="income" icon={Users2} delay={0} value={ops ? fmtNum(ops.enrolled) : "—"} />
      <KPI testId="op-kpi-booked" label={`Booked · ${year}`} tone="margin" icon={CalendarPlus} delay={0.04} value={ops ? fmtNum(ops.booked) : "—"} />
      <KPI testId="op-kpi-attended" label={`Attended · ${year}`} tone="profit" icon={Boxes} delay={0.08} value={ops ? fmtNum(ops.attended) : "—"} />
      <KPI testId="op-kpi-invoiced" label={`Invoiced · ${year}`} tone="income" icon={Receipt} delay={0.12} value={inv ? fmtCurrency(inv.totals.invoicedAmt) : "—"} />
    </div>
  );
}

function SessionShare({ sessionShare }) {
  if (!sessionShare.length) return null;
  return (
    <div className="mt-6" data-testid="session-share">
      <p className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)] mb-3">Session Popularity by Term (share of programme attendance)</p>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {sessionShare.map((ps) => (
          <div key={ps.programme} className="rounded-xl border border-[var(--hp-border)] overflow-hidden" data-testid={`session-share-${ps.programme.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="px-4 py-2.5 bg-[var(--hp-bg)]/40 border-b border-[var(--hp-border)] flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: PROGRAMME_COLORS[ps.programme] }} />
              <span className="text-sm font-semibold text-[var(--hp-heading)]">{ps.programme}</span>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-[var(--hp-muted)] border-b border-[var(--hp-border)]">
                  <th className="px-4 py-2 text-left font-semibold">Session</th>
                  <th className="px-3 py-2 text-right font-semibold">Kids</th>
                  <th className="px-3 py-2 text-right font-semibold">Avg/day</th>
                  <th className="px-3 py-2 text-right font-semibold">Fill</th>
                  <th className="px-3 py-2 text-right font-semibold">Share</th>
                  <th className="px-3 py-2 text-right font-semibold">Prev term</th>
                  <th className="px-3 py-2 text-right font-semibold">Same term last yr</th>
                </tr>
              </thead>
              <tbody>
                {ps.terms.map((t) => (
                  <Fragment key={t.term}>
                    <tr className="bg-[var(--hp-bg)]/20 border-b border-[var(--hp-border)]/40">
                      <td className="px-4 py-1.5" colSpan={2}><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${termChipClass(t.term)}`}>{t.term}</span> <span className="text-[var(--hp-muted)] ml-1">{t.total} kids</span></td>
                      <td className="px-3 py-1.5 text-right text-xs text-[var(--hp-muted)]" colSpan={5}>{t.prevTerm ? `vs ${t.prevTerm}` : "no previous term"}{t.hasLastYear ? "" : " · no last-year data"}</td>
                    </tr>
                    {t.sessions.map((s) => (
                      <tr key={s.sessionId} className="border-b border-[var(--hp-border)]/30">
                        <td className="px-4 py-1.5 text-[var(--hp-heading)]">{s.name}</td>
                        <td className="px-3 py-1.5 text-right font-mono-num text-[var(--hp-heading)]">{s.attendance}</td>
                        <td className="px-3 py-1.5 text-right font-mono-num text-[var(--hp-muted)]">{s.avgPerDay}</td>
                        <td className="px-3 py-1.5 text-right" data-testid={`fill-rate-${s.sessionId}`}>
                          {s.fillRate == null ? <span className="text-[var(--hp-muted)] text-xs">set capacity</span> : (
                            <span className={`font-mono-num font-semibold ${s.fillRate >= 0.85 ? "text-[var(--hp-profit)]" : s.fillRate >= 0.5 ? "text-[var(--hp-gold)]" : "text-[var(--hp-loss)]"}`}>{fmtPercent(s.fillRate)}<span className="text-xs text-[var(--hp-muted)] font-normal"> /{s.capacity}</span></span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <span className="inline-flex items-center gap-2 justify-end">
                            <span className="h-1.5 w-16 rounded-full bg-[var(--hp-tint-5)] overflow-hidden"><span className="block h-full rounded-full" style={{ width: `${Math.round(s.share * 100)}%`, background: PROGRAMME_COLORS[ps.programme] }} /></span>
                            <span className="font-mono-num font-semibold text-[var(--hp-heading)] w-12 text-right">{fmtPercent(s.share)}</span>
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-right">{shareDelta(s.share, s.prevTermShare)}</td>
                        <td className="px-3 py-1.5 text-right">{shareDelta(s.share, s.lastYearShare)}</td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

function TermComparison({ rows, year, hasLastYear }) {
  if (!rows.length) return null;
  const data = rows.map((r) => ({ ...r, label: r.term.replace(" Holidays", " Hols").replace("Summer Holidays", "Summer") }));
  const growth = (cur, last) => (last ? `${cur >= last ? "+" : ""}${(((cur - last) / Math.abs(last)) * 100).toFixed(0)}%` : null);
  return (
    <div className="mt-6" data-testid="term-comparison">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)]">Term Comparison — {year} vs {year - 1}</p>
        {!hasLastYear && <span className="text-xs text-[var(--hp-muted)]" data-testid="term-comparison-no-last-year">No {year - 1} data yet — add the year and log activities to compare</span>}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {[["Attendance", "attendance", "lastAttendance", (v) => fmtNum(v), "#22D3EE"], ["Profit", "profit", "lastProfit", (v) => fmtCurrency(v), "#34D399"]].map(([title, k, lk, fmt, color]) => (
          <div key={k}>
            <p className="text-xs text-[var(--hp-muted)] mb-2">{title} per term</p>
            <div className="h-[220px] w-full" data-testid={`term-comparison-${k}`}>
              <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 360, height: 240 }}>
                <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                  <XAxis dataKey="label" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => (k === "profit" ? fmtAxisMoney(v) : v)} />
                  <Tooltip contentStyle={tooltipStyleBase} labelStyle={{ color: "#E2E8F0", fontWeight: 700 }} formatter={(val, name) => [fmt(val), name]} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  <Bar dataKey={lk} name={String(year - 1)} fill="#475569" radius={[4, 4, 0, 0]} maxBarSize={26} />
                  <Bar dataKey={k} name={String(year)} fill={color} radius={[4, 4, 0, 0]} maxBarSize={26} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {rows.map((r) => {
          const ga = growth(r.attendance, r.lastAttendance), gp = growth(r.profit, r.lastProfit);
          return (
            <span key={r.term} data-testid={`term-growth-${r.term.replace(/\s+/g, "-").toLowerCase()}`} className="inline-flex items-center gap-2 rounded-full border border-[var(--hp-border)] bg-[var(--hp-bg)]/40 px-3 py-1 text-xs">
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${termChipClass(r.term)}`}>{r.term}</span>
              <span className="text-[var(--hp-muted)]">kids <span className="font-mono-num text-[var(--hp-heading)]">{fmtNum(r.attendance)}</span>{ga && <span className={`ml-1 font-mono-num ${ga.startsWith("+") ? "text-[var(--hp-profit)]" : "text-[var(--hp-loss)]"}`}>{ga}</span>}</span>
              <span className="text-[var(--hp-muted)]">profit <span className="font-mono-num text-[var(--hp-heading)]">{fmtCurrency(r.profit)}</span>{gp && <span className={`ml-1 font-mono-num ${gp.startsWith("+") ? "text-[var(--hp-profit)]" : "text-[var(--hp-loss)]"}`}>{gp}</span>}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

export function FundingReminders({ api, year, months }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${api}/funding/reminders`, { params: { year } }).then((r) => alive && setData(r.data)).catch(() => {});
    return () => { alive = false; };
  }, [api, year, months]);
  if (!data || !data.expected?.length) return null;
  const missing = data.missing || [];
  if (!missing.length) {
    return (
      <div className="rounded-2xl border border-[var(--hp-primary)]/30 bg-[var(--hp-primary)]/8 px-5 py-3.5 flex items-center gap-3 text-sm" data-testid="funding-reminders-ok">
        <HandCoins className="h-5 w-5 text-[var(--hp-profit)]" />
        <span className="text-[var(--hp-text)]">All expected funding recorded for {year} so far ({data.expected.map((e) => e.source).join(", ")}).</span>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-[var(--hp-gold)]/40 bg-[var(--hp-gold)]/8 px-5 py-4" data-testid="funding-reminders">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="h-5 w-5 text-[var(--hp-gold)]" />
        <p className="text-sm font-semibold text-[var(--hp-heading)]">Funding not yet recorded — {missing.length} item{missing.length > 1 ? "s" : ""}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {missing.map((m, i) => (
          <span key={i} data-testid={`funding-missing-${m.month}`} className="inline-flex items-center gap-2 rounded-full bg-[var(--hp-bg)]/60 border border-[var(--hp-gold)]/30 px-3 py-1 text-xs">
            <span className="font-semibold text-[var(--hp-gold)]">{m.month}</span>
            <span className="text-[var(--hp-text)]">{m.source}</span>
            {m.amount > 0 && <span className="font-mono-num text-[var(--hp-muted)]">~{fmtCurrency(m.amount)}</span>}
          </span>
        ))}
      </div>
      <p className="text-xs text-[var(--hp-muted)] mt-2">Record it under Data Input → Funding Income for that month, or adjust expected sources in Settings.</p>
    </div>
  );
}

function ProgrammeBreakdown({ api, year, progFilter = "", gstFactor = 1, termFilter = "" }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${api}/reports/programmes`, { params: { year, ...(termFilter ? { term: termFilter } : {}) } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => console.error(e));
    return () => { cancelled = true; };
  }, [api, year, termFilter]);

  const gx = (p) => {
    const income = p.income * gstFactor;
    const profit = income - p.expenses;
    return { ...p, income, profit, margin: income > 0 ? profit / income : 0 };
  };
  const progs = (data?.programmes || []).filter((p) => p.count > 0 && (!progFilter || p.name === progFilter)).map(gx);
  const monthlyCost = data?.monthlyCost || [];
  const monthlyIncome = data?.monthlyIncome || [];
  const monthlyAtt = data?.monthlyAttendance || [];
  const monthlyProfit = (data?.monthlyIncome || []).map((r, i) => ({ month: r.month, ...Object.fromEntries(FINANCE_PROGRAMMES.map((p) => [p, Math.round(((r[p] || 0) * gstFactor - (data?.monthlyCost?.[i]?.[p] || 0)) * 100) / 100])) }));
  const monthlyMargin = monthlyIncome.map((r, i) => ({ month: r.month, ...Object.fromEntries(FINANCE_PROGRAMMES.map((p) => {
    const inc = (r[p] || 0) * gstFactor, cost = monthlyCost[i]?.[p] || 0;
    return [p, inc > 0 ? (inc - cost) / inc : null];
  })) }));
  const runningAtt = data?.runningAvgAttendance || [];
  const runningStaff = data?.runningAvgStaff || [];
  const sessionShare = (data?.sessionShare || []).filter((ps) => !progFilter || ps.programme === progFilter);
  const termComparison = data?.termComparison || [];
  const hasLastYear = !!data?.hasLastYear;
  const byTerm = (data?.byTerm || []).map((t) => {
    const programmes = t.programmes.filter((p) => !progFilter || p.name === progFilter).map(gx);
    const income = programmes.reduce((s, p) => s + p.income, 0), expenses = programmes.reduce((s, p) => s + p.expenses, 0);
    return { ...t, programmes, income, expenses, profit: income - expenses, margin: income > 0 ? (income - expenses) / income : 0, attendance: programmes.reduce((s, p) => s + p.attendance, 0) };
  }).filter((t) => t.programmes.length);
  const activeProgs = progs.map((p) => p.name);
  const tooltipStyle = { background: "#0B1120", border: "1px solid #334155", borderRadius: 12, fontSize: 13 };

  return (
    <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 sm:p-6" data-testid="programme-breakdown-section">
      <div className="flex items-center gap-2 mb-5">
        <Boxes className="h-5 w-5 text-[var(--hp-profit)]" />
        <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)]">Programme Breakdown</h3>
      </div>

      {progs.length === 0 ? (
        <div className="grid place-items-center py-12 text-center" data-testid="programme-empty-state">
          <Users2 className="h-9 w-9 text-[var(--hp-muted)] mb-3" />
          <p className="text-sm text-[var(--hp-muted)] max-w-md">
            Log activities under Before School, After School or Holiday Programme in the Data Input tab to see per-programme income, expenses, profit and attendance here.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {progs.map((p) => {
              const positive = p.profit >= 0;
              return (
                <div key={p.name} data-testid={`programme-card-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
                  className="rounded-xl border border-[var(--hp-border)] bg-[var(--hp-bg)] p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="h-3 w-3 rounded-full" style={{ background: PROGRAMME_COLORS[p.name] }} />
                    <p className="text-sm font-semibold text-[var(--hp-heading)]">{p.name}</p>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-[var(--hp-muted)]">Income</span><span className="font-mono-num text-[var(--hp-accent)]">{fmtCurrency(p.income)}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--hp-muted)]">Expenses</span><span className="font-mono-num text-[var(--hp-gold)]">{fmtCurrency(p.expenses)}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--hp-muted)]">Profit</span><span className={`font-mono-num font-semibold ${positive ? "text-[var(--hp-profit)]" : "text-[var(--hp-loss)]"}`}>{fmtCurrency(p.profit)}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--hp-muted)]">Margin</span><span data-testid={`programme-margin-${p.name.replace(/\s+/g, "-").toLowerCase()}`} className={`font-mono-num font-semibold ${p.margin >= 0 ? "text-[var(--hp-profit)]" : "text-[var(--hp-loss)]"}`}>{fmtPercent(p.margin)}</span></div>
                    <div className="flex justify-between pt-1.5 border-t border-[var(--hp-border)] mt-1.5"><span className="text-[var(--hp-muted)]">Attendance</span><span className="font-mono-num text-[var(--hp-heading)] font-semibold">{fmtNum(p.attendance)}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--hp-muted)]">Avg attendance / session</span><span data-testid={`programme-avg-att-${p.name.replace(/\s+/g, "-").toLowerCase()}`} className="font-mono-num text-[var(--hp-heading)]">{p.avgAttendance}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--hp-muted)]">Avg staff on floor</span><span data-testid={`programme-avg-staff-${p.name.replace(/\s+/g, "-").toLowerCase()}`} className="font-mono-num text-[var(--hp-heading)]">{p.avgStaff}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--hp-muted)]">Kids per staff</span><span className="font-mono-num text-[var(--hp-c-cyan)]">{p.childrenPerStaff ? `${p.childrenPerStaff}:1` : "—"}</span></div>
                    {(p.cancellations > 0 || p.latePickups > 0) && (
                      <div className="flex justify-between text-xs pt-1"><span className="text-[var(--hp-muted)]">Cancellations / Late pick-ups</span><span className="font-mono-num text-[var(--hp-muted)]">{p.cancellations} / {p.latePickups}</span></div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-6">
            <TrendLine title="Running Avg Attendance per Session" data={runningAtt} progs={activeProgs} testId="running-avg-attendance" fmt={(v) => v} />
            <TrendLine title="Running Avg Staff on the Floor" data={runningStaff} progs={activeProgs} testId="running-avg-staff" fmt={(v) => v} />
          </div>

          <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)] mb-3">Profit Trend by Programme</p>
              <div className="h-[260px] w-full" data-testid="programme-profit-trend">
                <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 360, height: 240 }}>
                  <LineChart data={monthlyProfit} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                    <XAxis dataKey="month" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => fmtAxisMoney(v)} />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "#E2E8F0", fontWeight: 700 }} formatter={(val, name) => [fmtCurrency(val), name]} />
                    <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                    {FINANCE_PROGRAMMES.filter((p) => activeProgs.includes(p)).map((p) => (
                      <Line key={p} type="monotone" dataKey={p} stroke={PROGRAMME_COLORS[p]} strokeWidth={2.5} dot={{ r: 3, fill: PROGRAMME_COLORS[p] }} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)] mb-3">Monthly Attendance by Programme</p>
              <div className="h-[260px] w-full">
                <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 360, height: 240 }}>
                  <BarChart data={monthlyAtt} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                    <XAxis dataKey="month" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "#E2E8F0", fontWeight: 700 }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                    <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                    {FINANCE_PROGRAMMES.filter((p) => activeProgs.includes(p)).map((p) => (
                      <Bar key={p} dataKey={p} fill={PROGRAMME_COLORS[p]} radius={[3, 3, 0, 0]} maxBarSize={18} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {!progFilter && <TermComparison rows={termComparison.filter((r) => !termFilter || r.term === termFilter).map((r) => ({ ...r, profit: r.income * gstFactor - (r.income - r.profit), lastProfit: r.lastIncome * gstFactor - (r.lastIncome - r.lastProfit) }))} year={year} hasLastYear={hasLastYear} />}
          <MarginHeatmap monthlyMargin={monthlyMargin} progs={activeProgs} />
          <SessionShare sessionShare={sessionShare} />

          {byTerm.length > 0 && (
            <div className="mt-6" data-testid="term-breakdown">
              <p className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)] mb-3">Breakdown by School Term</p>
              <div className="overflow-x-auto rounded-xl border border-[var(--hp-border)]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-[var(--hp-muted)] border-b border-[var(--hp-border)] bg-[var(--hp-bg)]/40">
                      <th className="px-4 py-2.5 font-semibold">Term</th>
                      <th className="px-4 py-2.5 font-semibold">Programme</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Sessions</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Attendance</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Avg / Sess</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Avg Staff</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Income</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Expenses</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Profit</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byTerm.map((t) => (
                      <Fragment key={t.term}>
                        {t.programmes.map((p, i) => (
                          <tr key={`${t.term}-${p.name}`} data-testid={`term-row-${t.term.replace(/\s+/g, "-").toLowerCase()}-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
                            className="border-b border-[var(--hp-border)]/40 hover:bg-[var(--hp-tint-3)] transition-colors duration-150">
                            <td className="px-4 py-2.5">{i === 0 && <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${termChipClass(t.term)}`}>{t.term}</span>}</td>
                            <td className="px-4 py-2.5 text-[var(--hp-heading)]"><span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: PROGRAMME_COLORS[p.name] }} />{p.name}</span></td>
                            <td className="px-4 py-2.5 text-right font-mono-num text-[var(--hp-muted)]">{p.count}</td>
                            <td className="px-4 py-2.5 text-right font-mono-num text-[var(--hp-heading)]">{fmtNum(p.attendance)}</td>
                            <td className="px-4 py-2.5 text-right font-mono-num text-[var(--hp-heading)]">{p.avgAttendance}</td>
                            <td className="px-4 py-2.5 text-right font-mono-num text-[var(--hp-c-cyan)]">{p.avgStaff}</td>
                            <td className="px-4 py-2.5 text-right font-mono-num text-[var(--hp-accent)]">{fmtCurrency(p.income)}</td>
                            <td className="px-4 py-2.5 text-right font-mono-num text-[var(--hp-gold)]">{fmtCurrency(p.expenses)}</td>
                            <td className={`px-4 py-2.5 text-right font-mono-num font-semibold ${p.profit >= 0 ? "text-[var(--hp-profit)]" : "text-[var(--hp-loss)]"}`}>{fmtCurrency(p.profit)}</td>
                            <td className={`px-4 py-2.5 text-right font-mono-num ${p.margin >= 0 ? "text-[var(--hp-profit)]" : "text-[var(--hp-loss)]"}`}>{fmtPercent(p.margin)}</td>
                          </tr>
                        ))}
                        <tr className="border-b border-[var(--hp-border)] bg-[var(--hp-bg)]/30 text-xs">
                          <td className="px-4 py-2 text-[var(--hp-muted)] font-semibold uppercase tracking-wider" colSpan={3}>{t.term} total</td>
                          <td className="px-4 py-2 text-right font-mono-num text-[var(--hp-heading)] font-semibold">{fmtNum(t.attendance)}</td>
                          <td colSpan={2} />
                          <td className="px-4 py-2 text-right font-mono-num text-[var(--hp-accent)] font-semibold">{fmtCurrency(t.income)}</td>
                          <td className="px-4 py-2 text-right font-mono-num text-[var(--hp-gold)] font-semibold">{fmtCurrency(t.expenses)}</td>
                          <td className={`px-4 py-2 text-right font-mono-num font-bold ${t.profit >= 0 ? "text-[var(--hp-profit)]" : "text-[var(--hp-loss)]"}`}>{fmtCurrency(t.profit)}</td>
                          <td className={`px-4 py-2 text-right font-mono-num font-semibold ${t.margin >= 0 ? "text-[var(--hp-profit)]" : "text-[var(--hp-loss)]"}`}>{fmtPercent(t.margin)}</td>
                        </tr>
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const OVERHEAD_COLORS = ["#3B82F6", "#22D3EE", "#A78BFA", "#F59E0B", "#34D399", "#F472B6", "#FB923C", "#FCD34D", "#818CF8", "#60A5FA", "#2DD4BF", "#FB7185", "#A3E635", "#F97316"];

function OverheadsTrend({ months, cats = OVERHEAD_CATS }) {
  const backEnd = (m) => (m.overheads?.total || 0) - (m.overheads?.oneOffsTotal || 0);
  const data = months.map((m) => {
    const row = { month: m.month, "Admin Wages": m.overheads?.adminWages?.total || 0, "Leave Wages": m.overheads?.leaveWages?.total || 0, Funding: m.fundingTotal || 0 };
    cats.forEach((c) => { row[c.label] = m.overheads?.[c.key] || 0; });
    return row;
  });
  const total = months.reduce((s, m) => s + backEnd(m), 0);
  const peak = months.reduce((best, m) => (backEnd(m) > (best ? backEnd(best) : 0) ? m : best), null);
  return (
    <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 sm:p-6" data-testid="overheads-trend-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-[var(--hp-gold)]" />
          <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)]">Back-End Expenses Trend</h3>
        </div>
        <div className="flex items-center gap-5 text-xs text-[var(--hp-muted)]">
          <span>Year total <span data-testid="overheads-trend-total" className="font-mono-num font-bold text-[var(--hp-gold)]">{fmtCurrency(total)}</span></span>
          {peak && backEnd(peak) > 0 && <span>Peak <span className="font-semibold text-[var(--hp-heading)]">{peak.month}</span> · {fmtCurrency(backEnd(peak))}</span>}
        </div>
      </div>
      {total === 0 ? (
        <p className="text-sm text-[var(--hp-muted)] py-8 text-center" data-testid="overheads-trend-empty">No back-end expenses recorded yet. Set fixed monthly costs in Settings, or add admin hours per month in Finance → Back end expenses.</p>
      ) : (
        <div className="h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 360, height: 240 }}>
            <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
              <XAxis dataKey="month" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => fmtAxisMoney(v)} />
              <Tooltip contentStyle={tooltipStyleBase} labelStyle={{ color: "#E2E8F0", fontWeight: 700 }} formatter={(val, name) => [fmtCurrency(val), name]} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              {cats.map((c, i) => <Bar key={c.key} dataKey={c.label} stackId="o" fill={OVERHEAD_COLORS[i % OVERHEAD_COLORS.length]} maxBarSize={28} />)}
              <Bar dataKey="Admin Wages" stackId="o" fill="#E879F9" maxBarSize={28} />
              <Bar dataKey="Leave Wages" stackId="o" fill="#C084FC" radius={[4, 4, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function OneOffBreakdown({ months }) {
  const items = months.flatMap((m) => (m.overheads?.oneOffs || []).map((x) => ({ ...x, month: m.month })));
  const total = items.reduce((s, x) => s + (x.amount || 0), 0);
  return (
    <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 sm:p-6" data-testid="oneoff-breakdown-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Tag className="h-5 w-5 text-[var(--hp-gold)]" />
          <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)]">One-off Expenses Breakdown</h3>
        </div>
        <span className="text-xs text-[var(--hp-muted)]">Year total <span data-testid="oneoff-breakdown-total" className="font-mono-num font-bold text-[var(--hp-gold)]">{fmtCurrency(total)}</span></span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-[var(--hp-muted)] py-6 text-center" data-testid="oneoff-breakdown-empty">No one-off expenses recorded yet. Add them in Finance → Back end expenses.</p>
      ) : (
        <div className="divide-y divide-[var(--hp-border)]/60">
          {items.map((x, i) => (
            <div key={i} data-testid={`oneoff-breakdown-row-${i}`} className="flex items-center justify-between gap-3 py-2 text-sm">
              <span className="text-[var(--hp-muted)] w-14 shrink-0">{x.month}</span>
              <span className="text-[var(--hp-text)] flex-1 min-w-0 truncate">{x.note || "(no description)"}</span>
              <span className="font-mono-num text-[var(--hp-heading)] font-semibold">{fmtCurrency(x.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const TABS = [["overview", "Overview", LayoutDashboard], ["programmes", "Programmes", Boxes], ["ages", "Ages", Users2], ["cashflow", "Cash & Costs", Wallet]];

export default function Dashboard({ api, year, years, months, summary, settings, loading, reload, onYearChange, onAddYear, onDeleteYear, initialTab, onOpenRoll }) {
  const { user } = useAuth();
  const [tab, setTab] = useState(initialTab || "overview");
  const [showMonthlyTable, setShowMonthlyTable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progFilter, setProgFilter] = useState("");
  const [exGst, setExGst] = useState(false);
  // Default the Term Report picker to whatever term/holiday block is actually happening right now,
  // rather than always Term 1 -- opening this well into the year shouldn't pre-select a term that
  // finished months ago.
  // Settings usually finish loading after this screen mounts, so the default is worked out each render
  // (until the person picks a term) instead of captured once at mount, when it fell back to Term 1.
  const [pickedTerm, setReportTerm] = useState("");
  const reportTerm = pickedTerm || currentTerm(settings?.termsForYear, TERMS) || "Term 1";
  const [reportOpen, setReportOpen] = useState(false);
  const [termFilter, setTermFilter] = useState("");
  const [progReport, setProgReport] = useState(null);
  const [hasSpecial, setHasSpecial] = useState(false);       // the Special days filter only appears once the provider has a special-day programme
  useEffect(() => { axios.get(`${api}/programmes`).then((r) => setHasSpecial((Array.isArray(r.data) ? r.data : []).some((p) => p.kind === "special"))).catch(() => setHasSpecial(false)); }, [api]);
  const [forecastMonthly, setForecastMonthly] = useState(null);
  const [compliance, setCompliance] = useState(null);
  useEffect(() => { axios.get(`${api}/compliance`).then((r) => setCompliance(r.data)).catch(() => {}); }, [api]);
  const gst = settings?.rates?.gst ?? 0.15;
  const gstFactor = exGst ? 1 / (1 + gst) : 1;

  useEffect(() => {
    if (!progFilter && !termFilter) { setProgReport(null); return; }
    axios.get(`${api}/reports/programmes`, { params: { year, ...(termFilter ? { term: termFilter } : {}) } }).then((r) => setProgReport(r.data)).catch(() => setProgReport(null));
  }, [api, year, termFilter, progFilter]);
  useEffect(() => {
    axios.get(`${api}/forecast/monthly`, { params: { year } }).then((r) => setForecastMonthly(r.data.hasForecast ? r.data.monthly : null)).catch(() => setForecastMonthly(null));
  }, [api, year, months]);

  const activityBased = !!(progFilter || termFilter);
  const inTerm = (m) => !termFilter || (progReport?.termMonths || []).includes(m.month);
  // Real per-month income (from bookings/invoices), summed to whichever programme(s) are in view —
  // the same real figures ProgrammeBreakdown below already renders.
  const realIncomeByMonth = Object.fromEntries((progReport?.monthlyIncome || []).map((r) => [r.month, progFilter ? (r[progFilter] || 0) : FINANCE_PROGRAMMES.reduce((s, p) => s + (r[p] || 0), 0)]));
  const viewMonths = months.map((m) => {
    if (!activityBased) {
      const inc = m.income * gstFactor;
      return { ...m, income: inc, profit: inc - m.expenses, margin: inc > 0 ? (inc - m.expenses) / inc : 0 };
    }
    const evs = (m.events || []).filter((e) => (!progFilter || e.programme === progFilter) && (!termFilter || e.term === termFilter));
    const inc = (realIncomeByMonth[m.month] || 0) * gstFactor;
    const exp = evs.reduce((s, e) => s + e.cost, 0);
    return { ...m, income: inc, expenses: exp, profit: inc - exp, margin: inc > 0 ? (inc - exp) / inc : 0, eventCount: evs.length, events: evs,
      fundingTotal: inTerm(m) ? m.fundingTotal : 0, overheads: inTerm(m) ? m.overheads : { ...m.overheads, total: 0, oneOffsTotal: 0, oneOffs: [] } };
  }).filter((m) => !termFilter || inTerm(m) || m.eventCount > 0 || (realIncomeByMonth[m.month] || 0) > 0);
  const viewSummary = summary ? {
    total_income: viewMonths.reduce((s, m) => s + m.income, 0),
    total_expenses: viewMonths.reduce((s, m) => s + m.expenses, 0),
    total_funding: progFilter ? 0 : (termFilter ? viewMonths.reduce((s, m) => s + (m.fundingTotal || 0), 0) : (summary.total_funding || 0)) * gstFactor,
    total_overheads: progFilter ? 0 : termFilter ? viewMonths.reduce((s, m) => s + ((m.overheads?.total || 0) - (m.overheads?.oneOffsTotal || 0)), 0) : summary.total_overheads || 0,
    total_one_offs: progFilter ? 0 : termFilter ? viewMonths.reduce((s, m) => s + (m.overheads?.oneOffsTotal || 0), 0) : summary.total_one_offs || 0,
  } : null;
  if (viewSummary) {
    viewSummary.net_profit = viewSummary.total_income - viewSummary.total_expenses;
    viewSummary.avg_margin = viewSummary.total_income > 0 ? viewSummary.net_profit / viewSummary.total_income : 0;
  }

  const handleRefresh = async () => {
    setBusy(true);
    await reload();
    setBusy(false);
    toast.success("Dashboard data refreshed");
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${api}/export`, { params: { year }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileBase(user?.tenant_name)}_Dashboard_${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const fcByMonth = Object.fromEntries((forecastMonthly || []).map((f) => [f.month, f]));
  const showForecast = !!forecastMonthly && !progFilter;
  const chartData = viewMonths.map((m) => ({
    month: m.month,
    Income: m.income,
    Expenses: m.expenses,
    Profit: m.profit,
    ...(showForecast && fcByMonth[m.month] ? { "Forecast Income": fcByMonth[m.month].income * gstFactor, "Forecast Profit": fcByMonth[m.month].income * gstFactor - fcByMonth[m.month].expenses } : {}),
  }));

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Performance overview" title={`${year} Annual Dashboard`} testId="dashboard-header"
        description={<>Monthly income, expenses &amp; profitability for {user?.tenant_name || "your programme"}. <span data-testid="dashboard-income-basis-note">Income is counted in the month a child attended; Finance → Dashboard counts it by invoice date, so the two can differ.</span></>}
        actions={<>
          <YearSelector year={year} years={years} onYearChange={onYearChange} onAddYear={onAddYear} onDeleteYear={onDeleteYear} />
          <button data-testid="refresh-data-button" onClick={handleRefresh} disabled={busy}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--hp-border-hi)] bg-[var(--hp-card)] px-4 py-2.5 text-sm font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-card-hover)] transition-colors duration-200 disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button data-testid="export-excel-button" onClick={handleExport}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--hp-primary)] px-4 py-2.5 text-sm font-semibold text-[#04231a] hover:bg-[var(--hp-primary-hover)] transition-colors duration-200">
            <Download className="h-4 w-4" /> Export Excel
          </button></>}>
        <SubTabs tabs={asTabs(TABS)} value={tab} onChange={setTab} testId="dashboard-subtabs" itemTestId={(k) => `dashboard-subtab-${k}`} />
      </PageHeader>

      {/* Filters (money and programme filters: not for the Ages tab, which has its own) */}
      {tab !== "ages" && <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] px-4 py-3" data-testid="dashboard-filters">
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-[var(--hp-muted)]" />
          <button data-testid="filter-programme-all" onClick={() => setProgFilter("")}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${!progFilter ? "bg-[var(--hp-tint-10)] text-[var(--hp-heading)] ring-1 ring-[var(--hp-border-hi)]" : "text-[var(--hp-muted)] hover:text-[var(--hp-heading)]"}`}>All programmes</button>
          {FINANCE_PROGRAMMES.filter((p) => p !== "Special days" || hasSpecial).map((p) => (
            <button key={p} data-testid={`filter-programme-${p.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setProgFilter(p)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${progFilter === p ? "text-[var(--hp-heading)]" : "text-[var(--hp-muted)] hover:text-[var(--hp-heading)]"}`}
              style={progFilter === p ? { background: PROGRAMME_COLORS[p] + "26", boxShadow: `inset 0 0 0 1px ${PROGRAMME_COLORS[p]}` } : {}}>
              <span className="h-2 w-2 rounded-full" style={{ background: PROGRAMME_COLORS[p] }} />{p}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <TermFilter value={termFilter} onChange={setTermFilter} idPrefix="dash-" />
          <div className="inline-flex rounded-full border border-[var(--hp-border-hi)] bg-[var(--hp-bg)] p-0.5 text-xs font-semibold" data-testid="gst-toggle">
            <button data-testid="gst-incl-button" onClick={() => setExGst(false)} className={`rounded-full px-3 py-1.5 transition-colors ${!exGst ? "bg-[var(--hp-primary)] text-[#04231a]" : "text-[var(--hp-muted)] hover:text-[var(--hp-heading)]"}`}>Incl. GST</button>
            <button data-testid="gst-excl-button" onClick={() => setExGst(true)} className={`rounded-full px-3 py-1.5 transition-colors ${exGst ? "bg-[var(--hp-primary)] text-[#04231a]" : "text-[var(--hp-muted)] hover:text-[var(--hp-heading)]"}`}>Excl. GST ({Math.round(gst * 100)}%)</button>
          </div>
          <select data-testid="term-report-select" value={reportTerm} onChange={(e) => setReportTerm(e.target.value)}
            className="rounded-full border border-[var(--hp-border-hi)] bg-[var(--hp-bg)] px-3 py-1.5 text-xs font-semibold text-[var(--hp-heading)] outline-none">
            {TERMS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <button data-testid="term-report-button" onClick={() => setReportOpen(true)}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--hp-border-hi)] bg-[var(--hp-card)] px-4 py-1.5 text-xs font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-card-hover)] transition-colors">
            <ClipboardList className="h-3.5 w-3.5" /> Term Report
          </button>
        </div>
      </div>}
      {tab !== "ages" && (progFilter || exGst || termFilter) && (
        <p className="text-xs text-[var(--hp-muted)] -mt-4" data-testid="filter-note">
          Showing {progFilter ? <span className="text-[var(--hp-heading)] font-semibold">{progFilter}</span> : "all programmes"}{termFilter ? <> in <span className="text-[var(--hp-heading)] font-semibold">{termFilter}</span>{progReport?.termMonths?.length ? ` (${progReport.termMonths.join(", ")})` : ""}</> : ""}{activityBased ? " — activity-based figures" : ""}{exGst ? <> · income shown <span className="text-[var(--hp-heading)] font-semibold">excl. GST</span>, expenses unchanged</> : ""}
        </p>
      )}
      {reportOpen && <TermReport api={api} year={year} term={reportTerm} onClose={() => setReportOpen(false)} />}

      {tab === "overview" && (
        <>
          <DailyProgrammeBookings api={api} onOpenRoll={onOpenRoll} />
          <OperationalMetrics api={api} year={year} />
          <FundingReminders api={api} year={year} months={months} />
          {compliance && (compliance.counts.expired + compliance.counts.urgent + compliance.counts.warning) > 0 && (
            <div className="rounded-2xl border border-[var(--hp-gold)]/40 bg-[var(--hp-gold)]/10 px-5 py-3.5 flex items-center gap-3 text-sm" data-testid="compliance-alert">
              <AlertTriangle className="h-5 w-5 text-[var(--hp-gold)] shrink-0" />
              <span className="text-[var(--hp-text)]"><span className="font-semibold text-[var(--hp-gold)]">Staff compliance:</span> {[
                compliance.counts.expired && `${compliance.counts.expired} expired`, compliance.counts.urgent && `${compliance.counts.urgent} expiring within ${compliance.urgentDays} days`,
                compliance.counts.warning && `${compliance.counts.warning} expiring within ${compliance.warnDays} days`].filter(Boolean).join(", ")} — {compliance.reminders.filter((r) => r.level !== "missing").slice(0, 3).map((r) => `${r.name}: ${r.label}`).join(" · ")}. Manage documents on the Staff tab.</span>
            </div>
          )}
          {(() => {
            if (!forecastMonthly || year !== nzNow().getFullYear()) return null;
            const idx = nzNow().getMonth();
            const next2 = forecastMonthly.slice(idx + 1, idx + 3).filter((f) => f.netProfit < 0);
            return next2.length ? (
              <div className="rounded-2xl border border-[var(--hp-loss)]/40 bg-[var(--hp-loss)]/10 px-5 py-3.5 flex items-center gap-3 text-sm" data-testid="tight-month-alert">
                <AlertTriangle className="h-5 w-5 text-[var(--hp-loss)]" />
                <span className="text-[var(--hp-text)]"><span className="font-semibold text-[var(--hp-loss)]">Cash warning:</span> forecast goes negative in {next2.map((f) => `${f.month} (${fmtCurrency(f.netProfit)})`).join(" and ")} — review the roster, back-end costs or funding timing now.</span>
              </div>
            ) : null;
          })()}

          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 2xl:grid-cols-7 gap-4">
            <KPI testId="annual-kpi-income" label={`Total Income${exGst ? " (ex GST)" : ""}`} tone="income" icon={Wallet} delay={0.02}
              value={viewSummary ? fmtCurrency(viewSummary.total_income) : "—"} />
            <KPI testId="annual-kpi-expenses" label="Total Expenses" tone="expense" icon={Receipt} delay={0.08}
              value={viewSummary ? fmtCurrency(viewSummary.total_expenses) : "—"} />
            <KPI testId="annual-kpi-profit" label="Net Profit" tone="profit" icon={PiggyBank} delay={0.14}
              value={viewSummary ? fmtCurrency(viewSummary.net_profit) : "—"} />
            <KPI testId="annual-kpi-margin" label="Avg Margin" tone="margin" icon={Percent} delay={0.2}
              value={viewSummary ? fmtPercent(viewSummary.avg_margin) : "—"} />
            <KPI testId="annual-kpi-funding" label="Funding Income" tone="income" icon={HandCoins} delay={0.26}
              value={viewSummary ? fmtCurrency(viewSummary.total_funding) : "—"} />
            <KPI testId="annual-kpi-overheads" label="Back-End Expenses" tone="expense" icon={Building2} delay={0.32}
              value={viewSummary ? fmtCurrency(viewSummary.total_overheads) : "—"} />
            <KPI testId="annual-kpi-oneoffs" label="One-off Expenses" tone="expense" icon={Tag} delay={0.38}
              value={viewSummary ? fmtCurrency(viewSummary.total_one_offs) : "—"} />
          </div>

          {/* Chart, with the same monthly numbers foldable underneath instead of a separate table further down */}
          <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 sm:p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)]">Income vs Expenses & Profit Trend</h3>
            </div>
            <div className="h-[320px] w-full">
              <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 360, height: 240 }}>
                <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                  <XAxis dataKey="month" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false}
                    tickFormatter={(v) => fmtAxisMoney(v)} />
                  <Tooltip
                    contentStyle={{ background: "#0B1120", border: "1px solid #334155", borderRadius: 12, fontSize: 13 }}
                    labelStyle={{ color: "#E2E8F0", fontWeight: 700 }}
                    formatter={(val, name) => [fmtCurrency(val), name]} />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  <Bar dataKey="Income" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={22} />
                  <Bar dataKey="Expenses" fill="#F59E0B" radius={[4, 4, 0, 0]} maxBarSize={22} />
                  <Bar dataKey="Profit" fill="#34D399" radius={[4, 4, 0, 0]} maxBarSize={22} />
                  {showForecast && <Line type="monotone" dataKey="Forecast Income" stroke="#22D3EE" strokeWidth={1.5} strokeDasharray="6 4" dot={false} />}
                  {showForecast && <Line type="monotone" dataKey="Forecast Profit" stroke="#A78BFA" strokeWidth={1.5} strokeDasharray="6 4" dot={false} />}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <button data-testid="toggle-monthly-table" onClick={() => setShowMonthlyTable((v) => !v)}
              className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)] hover:text-[var(--hp-heading)]">
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showMonthlyTable ? "rotate-180" : ""}`} /> {showMonthlyTable ? "Hide" : "View"} monthly numbers
            </button>
            {showMonthlyTable && (
              <div className="overflow-x-auto mt-3 rounded-xl border border-[var(--hp-border)]">
                <table data-testid="annual-dashboard-table" className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-[var(--hp-muted)] border-b border-[var(--hp-border)] bg-[var(--hp-bg)]/40">
                      <th className="px-6 py-3 font-semibold">Month</th>
                      <th className="px-6 py-3 font-semibold text-right">Actual Income</th>
                      <th className="px-6 py-3 font-semibold text-right">Actual Expenses</th>
                      <th className="px-6 py-3 font-semibold text-right">Actual Profit</th>
                      <th className="px-6 py-3 font-semibold text-right">Margin %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={5} className="px-6 py-10 text-center text-[var(--hp-muted)]">Loading…</td></tr>
                    )}
                    {!loading && viewMonths.map((m) => {
                      const positive = m.profit >= 0;
                      return (
                        <tr key={m.month} data-testid={`row-${m.month}`}
                          className="border-b border-[var(--hp-border)]/60 hover:bg-[var(--hp-tint-3)] transition-colors duration-150">
                          <td className="px-6 py-3.5 font-semibold text-[var(--hp-heading)]">
                            <div className="flex items-center gap-2">
                              {m.month}
                              {m.eventCount > 0 && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-[var(--hp-tint-5)] px-2 py-0.5 text-xs font-medium text-[var(--hp-muted)]">
                                  <Layers className="h-3 w-3" /> {m.eventCount}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-3.5 text-right font-mono-num text-[var(--hp-text)]">{fmtCurrency(m.income)}</td>
                          <td className="px-6 py-3.5 text-right font-mono-num text-[var(--hp-text)]">{fmtCurrency(m.expenses)}</td>
                          <td className={`px-6 py-3.5 text-right font-mono-num font-semibold ${positive ? "text-[var(--hp-profit)]" : "text-[var(--hp-loss)]"}`}>
                            {fmtCurrency(m.profit)}
                          </td>
                          <td className="px-6 py-3.5 text-right">
                            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold font-mono-num ${
                              positive ? "bg-[var(--hp-primary)]/12 text-[var(--hp-profit)]" : "bg-[var(--hp-loss)]/12 text-[var(--hp-loss)]"}`}>
                              {positive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                              {fmtPercent(m.margin)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <YearComparison api={api} years={years} currentYear={year} />
        </>
      )}

      {tab === "programmes" && (
        <ProgrammeBreakdown api={api} year={year} progFilter={progFilter} gstFactor={gstFactor} termFilter={termFilter} />
      )}

      {tab === "ages" && <AgeBreakdown api={api} years={years} currentYear={year} />}
      {tab === "cashflow" && (
        <>
          {progFilter ? (
            <p className="text-sm text-[var(--hp-muted)] rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5" data-testid="cashflow-progfilter-note">Cash flow isn't split by programme — clear the programme filter above to see it.</p>
          ) : (
            <CashFlow months={termFilter ? viewMonths : months} gstFactor={gstFactor} forecast={forecastMonthly} />
          )}
          <OverheadsTrend months={termFilter ? viewMonths : months} cats={settings?.overheadCategories || OVERHEAD_CATS} />
          <OneOffBreakdown months={termFilter ? viewMonths : months} />
        </>
      )}
    </div>
  );
}
